import { sanitiseHrefTel } from '../../tel/sanitise';
import { getUser } from '../../user/user';
import { getCommonElementParameters, triggerTWCEvent, TWCCallEvent, TWCCallEventV2, TWCEvent, TWCEventName, TWCEventParameters, TWCRevealTelephoneEvent, TWCRevealTelephoneEventV2 } from './events';

type Matcher = {
    match: ( t: EventTarget ) => boolean;
    event: ( element: HTMLElement ) => Array<TWCEvent<TWCEventParameters>> | Promise<Array<TWCEvent<TWCEventParameters>>>;
}

const eventHandlerMapping: Array<Matcher> = [
    {
        match: ( t: EventTarget ) => {
            return ( t instanceof HTMLButtonElement && t.classList.contains( 'clicktoreveal' ) );
        },
        event: ( element: HTMLElement ): Array<TWCEvent<TWCRevealTelephoneEvent | TWCRevealTelephoneEventV2>> => {
            const user = getUser();

            const origins: Array<string> = [];

            if ( user.phoneOne ) {
                origins.push( user.phoneOne );
            }

            if ( user.phoneTwo && user.phoneTwo !== user.phoneOne ) {
                origins.push( user.phoneTwo );
            }

            const elementWithItemID = element.closest<HTMLElement>( '[data-property-id],[data-directory-id]' );
            const elementWithClientIDAndItemType = element.closest<HTMLElement>( '[data-client-id]' );

            if ( !elementWithItemID ) {
                throw new Error( 'no parent [data-property-id] element found' );
            }

            if ( !elementWithClientIDAndItemType ) {
                throw new Error( 'no parent [data-client-id] element found' );
            }

            let id = '';

            if ( elementWithItemID.dataset.propertyId ) {
                id = elementWithItemID.dataset.propertyId;
            } else if ( elementWithItemID.dataset.directoryId ) {
                id = elementWithItemID.dataset.directoryId;
            }

            const hrefValues = element.getAttribute( 'data-href' );

            if ( !hrefValues ) {
                throw new Error( 'no data-href values found' );
            }

            const destinations: Array<string> = [];

            hrefValues.split( ',' ).forEach( ( hrefStr: string ) => {
                if ( /tel:/.test( element.getAttribute( 'data-href' ) as string ) ) {
                    destinations.push( sanitiseHrefTel( hrefStr ) );
                } else {
                    const hrefURL = new URL( hrefStr );
                    destinations.push( hrefURL.pathname.split( '/' )[ hrefURL.pathname.split( '/' ).length - 1 ] );
                }
            } );

            const commonParams = getCommonElementParameters( element );

            const events: Array<TWCEvent<TWCRevealTelephoneEvent | TWCRevealTelephoneEventV2>> = destinations.map( ( destination: string ) => {
                return [
                    {
                        eventName: TWCEventName.RevealNumber,
                        [ TWCEventName.RevealNumber ]: {
                            destination: destination,
                            itemID: parseInt( id || '0' ),
                            itemType: elementWithClientIDAndItemType.dataset.itemType || '',
                            clientID: elementWithClientIDAndItemType.dataset.clientId || '',
                            type: element.dataset.revealType || 'phone',
                        },
                    },
                    {
                        eventName: TWCEventName.RevealNumberV2,
                        [ TWCEventName.RevealNumberV2 ]: {
                            ...commonParams,
                            destination: destination,
                            destinationType: [ element.dataset.revealType || 'phone' ],
                        },
                    },
                ];
            } ).reduce( ( pv, cv ) => {
                return [ ...pv, ...cv ];
            }, [] );

            return events;
        },
    },
    {
        match: ( t: EventTarget ) => {
            return ( t instanceof HTMLAnchorElement && t.href.startsWith( 'tel:' ) );
        },
        event: async ( element: HTMLElement ): Promise<Array<TWCEvent<TWCCallEvent | TWCCallEventV2>>> => {
            const user = getUser();

            const origins: Array<string> = [];

            if ( user.phoneOne ) {
                origins.push( user.phoneOne );
            }

            if ( user.phoneTwo && user.phoneTwo !== user.phoneOne ) {
                origins.push( user.phoneTwo );
            }

            const elementWithID = element.closest<HTMLElement>( '[data-property-id]' );

            const commonParams = getCommonElementParameters( element );

            const events: Array<TWCEvent<TWCCallEvent | TWCCallEventV2>> = [
                {
                    eventName: TWCEventName.CallV2,
                    [ TWCEventName.CallV2 ]: {
                        ...commonParams,
                        destination: sanitiseHrefTel( element.getAttribute( 'href' ) || '' ),
                        origins: origins,
                    },
                } as TWCEvent<TWCCallEventV2>,
            ];

            if ( elementWithID ) {
                events.push( {
                    eventName: TWCEventName.Call,
                    [ TWCEventName.Call ]: {
                        destination: sanitiseHrefTel( element.getAttribute( 'href' ) || '' ),
                        originCountry: ( await getUser().visitorInfo() ).country,
                        origins: origins,
                        itemID: parseInt( elementWithID.dataset.propertyId || '0' ),
                    },
                } as TWCEvent<TWCCallEvent> );
            }

            return events;
        },
    },
];

export function initTWCGlobalClickEvents(): void {
    document.addEventListener( 'click', ( event: Event ) => {
        if ( !event.target ) {
            return;
        }

        let element: EventTarget | null = event.target;

        const isClickable = ( event.target instanceof HTMLButtonElement || event.target instanceof HTMLAnchorElement );

        if ( !isClickable && event.target instanceof Element ) {
            element = ( event.target.closest<HTMLAnchorElement | HTMLButtonElement>( 'a,button' ) );
        }

        if ( !element || !( element instanceof HTMLElement ) ) {
            return;
        }

        eventHandlerMapping
            .filter( ( m: Matcher ) => {
                return m.match( element as EventTarget );
            } )
            .forEach( async ( m: Matcher ) => {
                const events = await m.event( element as HTMLElement );

                events.forEach( ( e ) => {
                    triggerTWCEvent( e.eventName, e[ e.eventName ] as TWCEventParameters );
                } );
            } );
    }, { passive: true } );
}
